import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SingleUnit, Tire } from '../types/schema';
import { useUserContext } from '../contexts/UserContext';
import { useEquipmentContext } from '../contexts/EquipmentContext';
import { authClient } from '../utils/amplifyClient';
import { getSingleUnit, getAxleBlueprint } from '../graphql/queries';
import { createAxleBlueprint, updateAxleBlueprint } from '../graphql/mutations';
import { AxleBlueprintBuilder, TIRE_GRAPHICS } from './AxleBlueprintBuilder';
import type { AxleBlueprint } from '../types/axleBlueprint';
import { formatDateDisplay } from '../utils/helperFunctions';

export const TiresTab = ({ unit }: { unit: SingleUnit }) => {
    const navigate = useNavigate();
    const [tires, setTires] = useState<Tire[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedTire, setExpandedTire] = useState<string | null>(null);
    const [showBuilder, setShowBuilder] = useState(false);
    const [fetchedBlueprint, setFetchedBlueprint] = useState<AxleBlueprint | null>(null);

    const { company, companyOwner } = useUserContext();
    const { updateUnitInDatabase } = useEquipmentContext();

    const companyId = (company?.id as string) || companyOwner?.companyId || '';


    useEffect(() => {
        const fetchTires = async () => {
            try {
                const result = await authClient.graphql({
                    query: getSingleUnit,
                    variables: { id: unit.id },
                });
                // Gen2 SingleUnit type has no `tires` relation and the query does not select one,
                // so this resolves to undefined at runtime (preserving prior behavior).
                const fetchedTires =
                    (result.data.getSingleUnit as { tires?: { items?: (Tire | null)[] } } | null | undefined)
                        ?.tires?.items?.filter(Boolean) as Tire[] || [];
                setTires(fetchedTires);
                if (fetchedTires.length > 0) {
                    setExpandedTire(fetchedTires[0].id);
                }
            } catch (err) {
                console.error('Error fetching tires:', err);
            } finally {
                setLoading(false);
            }
        };
        const fetchAxleConfig = async () => {
            if (!unit.axleConfiguration) return;
            try {
                const result = await authClient.graphql({
                    query: getAxleBlueprint,
                    variables: { id: unit.axleConfiguration },
                });
                const bp = result.data.getAxleBlueprint;
                if (bp) {
                    setFetchedBlueprint({
                        id: bp.id,
                        name: bp.name,
                        description: bp.description || '',
                        companyId: bp.companyId,
                        centerlineLength: bp.centerlineLength,
                        axles: typeof bp.axles === 'string' ? JSON.parse(bp.axles) : bp.axles,
                    });
                }
            } catch (err) {
                console.error('Error fetching axle blueprint:', err);
            }
        };
        fetchTires();
        fetchAxleConfig();
    }, [unit.id]);

    const installedTires = tires.filter(t => !t.removedFromService);

    const toggleTire = (tireId: string) => {
        setExpandedTire(prev => (prev === tireId ? null : tireId));
    };

    const handleBlueprintSave = async (blueprint: AxleBlueprint) => {
        if (!blueprint) return;

        try {
            const { createdAt: _createdAt, updatedAt: _updatedAt, ...rest } = blueprint;
            const axlesJson = JSON.stringify(blueprint.axles);

            if (fetchedBlueprint?.id) {
                await authClient.graphql({
                    query: updateAxleBlueprint,
                    variables: {
                        input: {
                            id: fetchedBlueprint.id,
                            name: rest.name,
                            companyId: rest.companyId,
                            centerlineLength: rest.centerlineLength,
                            axles: axlesJson,
                            description: rest.description || null,
                        }
                    }
                });
                setFetchedBlueprint({ ...blueprint, id: fetchedBlueprint.id });
            } else {
                const { id: _id, ...createInput } = rest;
                const blueprintResult = await authClient.graphql({
                    query: createAxleBlueprint,
                    variables: {
                        input: {
                            ...createInput,
                            axles: axlesJson,
                        }
                    }
                });

                const savedId = blueprintResult.data.createAxleBlueprint.id;
                await updateUnitInDatabase(unit.id, { axleConfiguration: savedId });
                setFetchedBlueprint({ ...blueprint, id: savedId });
            }
        } catch (err) {
            console.error('Error saving blueprint:', err);
        }

        setShowBuilder(false);
    };

    if (loading) {
        return <div className="tab-placeholder"><p>Loading tire data...</p></div>;
    }

    if (showBuilder) {
        return (
            <AxleBlueprintBuilder
                initialBlueprint={fetchedBlueprint}
                onSave={handleBlueprintSave}
                onCancel={() => setShowBuilder(false)}
                companyId={companyId}
                assetId={unit.name || unit.id}
            />
        );
    }

    return (
        <div className="tires-tab">
            <div className="tires-layout">
                {/* Left: Axle Configuration */}
                <div className="axle-config-section">
                    <div className="axle-config-header">
                        <h2>Axle Configuration</h2>
                        {fetchedBlueprint && (
                            <button className="edit-btn" onClick={() => setShowBuilder(true)}>Edit</button>
                        )}
                    </div>
                    <div className="axle-config-divider" />

                    {fetchedBlueprint ? (
                        <>
                            <div className="axle-config-label">
                                <span className="axle-config-name">{fetchedBlueprint.name}</span>
                                <span className="axle-config-desc">
                                    {fetchedBlueprint.axles.reduce((sum, a) => sum + a.tireSlots.length, 0)} wheel positions,{' '}
                                    {fetchedBlueprint.axles.length} axle{fetchedBlueprint.axles.length !== 1 ? 's' : ''}
                                </span>
                            </div>

                            <div className="axle-diagram">
                                {fetchedBlueprint.axles.map((axle, axleIdx) => {
                                    const leftSlots = axle.tireSlots.filter(s => s.xOffset < 0).sort((a, b) => a.xOffset - b.xOffset);
                                    const centerSlots = axle.tireSlots.filter(s => s.xOffset === 0);
                                    const rightSlots = axle.tireSlots.filter(s => s.xOffset > 0).sort((a, b) => a.xOffset - b.xOffset);

                                    return (
                                        <div key={axle.id} className="axle-group">
                                            <div className="axle-row">
                                                {/* Left-side tires */}
                                                <div className="tire-group left">
                                                    {leftSlots.map(slot => {
                                                        const assignedTire = installedTires.find(t => t.assignedPosition === slot.id || t.assignedPosition === slot.positionCode);
                                                        const def = TIRE_GRAPHICS[slot.tireGraphic];
                                                        return (
                                                            <div key={slot.id} className={`tire-slot-display ${assignedTire ? 'assigned' : ''}`}>
                                                                <div className="tire-graphic-box" style={{ width: Math.max(36, def?.width * 0.8 || 36), height: Math.max(44, def?.height * 0.8 || 44) }}>
                                                                    <div className="tire-tread-line" />
                                                                    <div className="tire-tread-line" />
                                                                    <div className="tire-tread-line" />
                                                                </div>
                                                                <span className="tire-pos-code">{slot.positionCode || '—'}</span>
                                                                <span className="tire-assigned-id">{assignedTire?.name || '—'}</span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>

                                                {/* Axle line with label */}
                                                <div className="axle-center">
                                                    {centerSlots.length > 0 && centerSlots.map(slot => {
                                                        const assignedTire = installedTires.find(t => t.assignedPosition === slot.id || t.assignedPosition === slot.positionCode);
                                                        const def = TIRE_GRAPHICS[slot.tireGraphic];
                                                        return (
                                                            <div key={slot.id} className={`tire-slot-display center ${assignedTire ? 'assigned' : ''}`}>
                                                                <div className="tire-graphic-box" style={{ width: Math.max(36, def?.width * 0.8 || 36), height: Math.max(44, def?.height * 0.8 || 44) }}>
                                                                    <div className="tire-tread-line" />
                                                                    <div className="tire-tread-line" />
                                                                    <div className="tire-tread-line" />
                                                                </div>
                                                                <span className="tire-pos-code">{slot.positionCode || '—'}</span>
                                                                <span className="tire-assigned-id">{assignedTire?.name || '—'}</span>
                                                            </div>
                                                        );
                                                    })}
                                                    <div className="axle-line-wrap">
                                                        <div className="axle-line-segment" />
                                                        <span className="axle-label-badge">{axle.label}</span>
                                                        <div className="axle-line-segment" />
                                                    </div>
                                                </div>

                                                {/* Right-side tires */}
                                                <div className="tire-group right">
                                                    {rightSlots.map(slot => {
                                                        const assignedTire = installedTires.find(t => t.assignedPosition === slot.id || t.assignedPosition === slot.positionCode);
                                                        const def = TIRE_GRAPHICS[slot.tireGraphic];
                                                        return (
                                                            <div key={slot.id} className={`tire-slot-display ${assignedTire ? 'assigned' : ''}`}>
                                                                <div className="tire-graphic-box" style={{ width: Math.max(36, def?.width * 0.8 || 36), height: Math.max(44, def?.height * 0.8 || 44) }}>
                                                                    <div className="tire-tread-line" />
                                                                    <div className="tire-tread-line" />
                                                                    <div className="tire-tread-line" />
                                                                </div>
                                                                <span className="tire-pos-code">{slot.positionCode || '—'}</span>
                                                                <span className="tire-assigned-id">{assignedTire?.name || '—'}</span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            {/* Vertical shaft between axles */}
                                            {axleIdx < fetchedBlueprint.axles.length - 1 && (
                                                <div className="axle-shaft-connector" />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    ) : (
                        <p className="no-config-msg">
                            No axle configuration set.{' '}
                            <button className="edit-btn" onClick={() => setShowBuilder(true)}>Build Configuration</button>
                        </p>
                    )}
                </div>

                {/* Right: Installed Tires */}
                <div className="installed-tires-section">
                    <div className="installed-tires-header">
                        <h2>Installed Tires ({installedTires.length})</h2>
                        <button className="add-tire-btn">
                            <span className="add-tire-icon">⊕</span> Add
                        </button>
                    </div>

                    {installedTires.length === 0 ? (
                        <p className="no-tires-msg">No tires currently installed on this unit.</p>
                    ) : (
                        <div className="tire-accordion">
                            {fetchedBlueprint?.axles.flatMap(axle =>
                                axle.tireSlots.map(slot => {
                                    const tire = installedTires.find(t => t.assignedPosition === slot.id || t.assignedPosition === slot.positionCode);
                                    return { slot, axle, tire };
                                })
                            ).map(({ slot, axle, tire }) => {
                                const isExpanded = tire ? expandedTire === tire.id : false;
                                const sideLabel = slot.xOffset < 0 ? 'Left' : slot.xOffset > 0 ? 'Right' : 'Center';
                                const axleShort = axle.label.replace(/\s*axle\s*/i, '').trim();
                                const displayName = `${sideLabel} ${axleShort}`;

                                return (
                                    <div key={slot.id} className={`tire-accordion-item ${isExpanded ? 'expanded' : ''}`}>
                                        <button
                                            className="tire-accordion-header"
                                            onClick={() => tire && toggleTire(tire.id)}
                                            aria-expanded={isExpanded}
                                            disabled={!tire}
                                        >
                                            <span className={`accordion-chevron ${isExpanded ? 'open' : ''}`}>›</span>
                                            <span className="tire-accordion-title">
                                                {displayName} ({slot.positionCode})
                                            </span>
                                            <span className="axle-badge">{axle.label}</span>
                                        </button>
                                        <span className="tire-accordion-subtitle">
                                            {tire?.name || <span className="empty-slot-label">Empty slot</span>}
                                        </span>

                                        {isExpanded && tire && (
                                            <div className="tire-detail-card">
                                                <div className="tire-detail-header">
                                                    <span className="tire-detail-name">{tire.name}</span>
                                                    <button className="edit-btn">Edit</button>
                                                </div>

                                                <div className="tire-measurements">
                                                    <div className="tire-measurement-box">
                                                        <span className="measurement-label">Current Tread Depth</span>
                                                        <div className="measurement-value-row">
                                                            <span className="measurement-value">{tire.currentTreadDepth ?? '—'}</span>
                                                            <span className="measurement-unit">/32 in</span>
                                                        </div>
                                                    </div>
                                                    <div className="tire-measurement-box">
                                                        <span className="measurement-label">Current Tire Pressure</span>
                                                        <div className="measurement-value-row">
                                                            <span className="measurement-value">{tire.currentTirePressure ?? '—'}</span>
                                                            <span className="measurement-unit">/{tire.recommendedTirePressure ?? '—'} PSI</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="tire-info-rows">
                                                    <div className="tire-info-row">
                                                        <span className="tire-info-label">Date Installed</span>
                                                        <span className="tire-info-value">{formatDateDisplay(tire.dateInstalled)}</span>
                                                    </div>
                                                    <div className="tire-info-row">
                                                        <span className="tire-info-label">Manufacturer</span>
                                                        <span className="tire-info-value">{tire.tireManufacturer || '—'}</span>
                                                    </div>
                                                    <div className="tire-info-row">
                                                        <span className="tire-info-label">Category</span>
                                                        <span className="tire-info-value">{tire.tireCategory || '—'}</span>
                                                    </div>
                                                    <div className="tire-info-row">
                                                        <span className="tire-info-label">Model</span>
                                                        <span className="tire-info-value">{tire.tireModel || '—'}</span>
                                                    </div>
                                                    <div className="tire-info-row">
                                                        <span className="tire-info-label">Size</span>
                                                        <span className="tire-info-value">{tire.tireSize || '—'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {/* Tires not matched to any slot */}
                            {installedTires
                                .filter(t => !fetchedBlueprint?.axles.some(a =>
                                    a.tireSlots.some(s => s.id === t.assignedPosition || s.positionCode === t.assignedPosition)
                                ))
                                .map(tire => {
                                    const isExpanded = expandedTire === tire.id;
                                    return (
                                        <div key={tire.id} className={`tire-accordion-item ${isExpanded ? 'expanded' : ''}`}>
                                            <button
                                                className="tire-accordion-header"
                                                onClick={() => toggleTire(tire.id)}
                                                aria-expanded={isExpanded}
                                            >
                                                <span className={`accordion-chevron ${isExpanded ? 'open' : ''}`}>›</span>
                                                <span className="tire-accordion-title">Unassigned</span>
                                            </button>
                                            <span className="tire-accordion-subtitle">{tire.name}</span>

                                            {isExpanded && (
                                                <div className="tire-detail-card">
                                                    <div className="tire-detail-header">
                                                        <span className="tire-detail-name">{tire.name}</span>
                                                        <button className="edit-btn">Edit</button>
                                                    </div>
                                                    <div className="tire-measurements">
                                                        <div className="tire-measurement-box">
                                                            <span className="measurement-label">Current Tread Depth</span>
                                                            <div className="measurement-value-row">
                                                                <span className="measurement-value">{tire.currentTreadDepth ?? '—'}</span>
                                                                <span className="measurement-unit">/32 in</span>
                                                            </div>
                                                        </div>
                                                        <div className="tire-measurement-box">
                                                            <span className="measurement-label">Current Tire Pressure</span>
                                                            <div className="measurement-value-row">
                                                                <span className="measurement-value">{tire.currentTirePressure ?? '—'}</span>
                                                                <span className="measurement-unit">/{tire.recommendedTirePressure ?? '—'} PSI</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="tire-info-rows">
                                                        <div className="tire-info-row">
                                                            <span className="tire-info-label">Date Installed</span>
                                                            <span className="tire-info-value">{formatDateDisplay(tire.dateInstalled)}</span>
                                                        </div>
                                                        <div className="tire-info-row">
                                                            <span className="tire-info-label">Manufacturer</span>
                                                            <span className="tire-info-value">{tire.tireManufacturer || '—'}</span>
                                                        </div>
                                                        <div className="tire-info-row">
                                                            <span className="tire-info-label">Category</span>
                                                            <span className="tire-info-value">{tire.tireCategory || '—'}</span>
                                                        </div>
                                                        <div className="tire-info-row">
                                                            <span className="tire-info-label">Model</span>
                                                            <span className="tire-info-value">{tire.tireModel || '—'}</span>
                                                        </div>
                                                        <div className="tire-info-row">
                                                            <span className="tire-info-label">Size</span>
                                                            <span className="tire-info-value">{tire.tireSize || '—'}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                        </div>
                    )}
                </div>
            </div>

            <div className="tire-module-link">
                <button className="link-button" onClick={() => navigate('/tire-maintenance')}>View Tire Maintenance Module →</button>
            </div>
        </div>
    );
};
